import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { History, RefreshCw, Trash2, ChevronDown, ChevronRight, Search, Save, ArrowRightLeft, X } from "lucide-react";
import { Button } from "../components/ui/button";

const WorkerHistory = () => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [query, setQuery] = useState("");
  const [diffResult, setDiffResult] = useState(null);
  const [diffLoadingId, setDiffLoadingId] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/machine-data/snapshots`);
      setSnapshots(r.data || []);
    } catch (e) {
      toast.error("Failed to load history");
    }
    setLoading(false);
  };

  const saveNow = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/machine-data/snapshot-now`, {});
      if (r.data?.success) {
        toast.success(`Saved a snapshot of ${r.data.count} machine(s)`);
        fetchAll();
      } else {
        toast.error(r.data?.error || "Nothing to save");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save snapshot");
    }
    setSaving(false);
  };

  const compareToNow = async (id) => {
    setDiffLoadingId(id);
    try {
      const r = await axios.get(`${API}/machine-data/snapshots/${id}/diff`);
      setDiffResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to compare");
    }
    setDiffLoadingId(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this snapshot from history?")) return;
    try {
      await axios.delete(`${API}/machine-data/snapshots/${id}`);
      setSnapshots(prev => prev.filter(s => s.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const filtered = query.trim()
    ? snapshots.filter(s => {
        const q = query.toLowerCase();
        if ((s.farm || "").toLowerCase().includes(q)) return true;
        return (s.entries || []).some(e =>
          (e.ip || "").toLowerCase().includes(q) ||
          (e.worker_name || "").toLowerCase().includes(q)
        );
      })
    : snapshots;

  return (
    <div className="space-y-6" data-testid="worker-history-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <History size={24} className="text-[#00E054]" /> Worker/IP History
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Dated snapshots of every worker name mapped to its IP — save one here using the
            latest synced data, or from the Mining Fleet desktop app's own snapshot button.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-[#27272A] text-gray-300"
            onClick={fetchAll}
            data-testid="btn-refresh-worker-history"
          >
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button
            className="bg-[#00E054] hover:bg-[#00E054]/80 text-black font-bold"
            onClick={saveNow}
            disabled={saving}
            data-testid="btn-save-snapshot-now"
          >
            <Save size={14} className="mr-1" /> {saving ? "Saving…" : "Save Snapshot Now"}
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-[#00E054]"
          placeholder="Search by farm, IP, or worker name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="worker-history-search"
        />
      </div>

      <div className="space-y-3">
        {loading ? (
          <p className="text-center py-10 text-gray-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl">
            <p className="text-center py-10 text-gray-500">
              {query ? "No matches" : "No snapshots yet. Click \"Save Worker/IP Snapshot\" in the desktop app to create one."}
            </p>
          </div>
        ) : (
          filtered.map((snap) => {
            const isOpen = expanded === snap.id;
            return (
              <div key={snap.id} className="bg-[#0A0A0A] border border-[#27272A] rounded-xl overflow-hidden" data-testid={`snapshot-${snap.id}`}>
                <button
                  className="w-full flex items-center justify-between gap-4 px-4 py-3 hover:bg-[#0F0F11] text-left"
                  onClick={() => setExpanded(isOpen ? null : snap.id)}
                  data-testid={`snapshot-toggle-${snap.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isOpen ? <ChevronDown size={16} className="text-gray-500 shrink-0" /> : <ChevronRight size={16} className="text-gray-500 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-white font-semibold truncate">{formatDate(snap.taken_at)}</p>
                      <p className="text-xs text-gray-400 truncate">{snap.farm || "Main Farm"} · {snap.count ?? (snap.entries || []).length} machine(s)</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button
                      size="sm" variant="outline"
                      className="border-cyan-500/30 text-cyan-400 text-xs h-7"
                      onClick={(e) => { e.stopPropagation(); compareToNow(snap.id); }}
                      disabled={diffLoadingId === snap.id}
                      data-testid={`btn-compare-snapshot-${snap.id}`}
                    >
                      <ArrowRightLeft size={12} className="mr-1" />
                      {diffLoadingId === snap.id ? "Comparing…" : "Compare to Now"}
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      className="border-red-500/30 text-red-400 text-xs h-7"
                      onClick={(e) => { e.stopPropagation(); remove(snap.id); }}
                      data-testid={`btn-del-snapshot-${snap.id}`}
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-[#27272A] data-table-wrap overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#18181B] text-gray-400 text-xs uppercase">
                        <tr>
                          <th className="text-left px-4 py-2">IP</th>
                          <th className="text-left px-4 py-2">Worker Name</th>
                          <th className="text-left px-4 py-2">SN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(snap.entries || []).map((e, i) => (
                          <tr key={`${e.ip}-${i}`} className="border-t border-[#27272A]">
                            <td className="px-4 py-2 font-mono text-xs text-gray-300">{e.ip}</td>
                            <td className="px-4 py-2 text-white">{e.worker_name || "—"}</td>
                            <td className="px-4 py-2 font-mono text-xs text-gray-400">{e.sn || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {diffResult && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" data-testid="diff-modal">
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#27272A] p-4 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <ArrowRightLeft size={18} className="text-cyan-400" /> Changes since {formatDate(diffResult.taken_at)}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {diffResult.unchanged_count} of {diffResult.total_entries} unchanged
                </p>
              </div>
              <Button size="sm" variant="ghost" className="text-gray-400" onClick={() => setDiffResult(null)}>
                <X size={16} />
              </Button>
            </div>
            <div className="p-5 space-y-5">
              {diffResult.ip_changed.length === 0 && diffResult.worker_changed.length === 0 && diffResult.went_offline.length === 0 && (
                <p className="text-center py-6 text-[#00E054] font-medium">✓ No changes — everything matches this snapshot</p>
              )}

              {diffResult.ip_changed.length > 0 && (
                <div>
                  <p className="text-xs uppercase font-semibold text-yellow-400 mb-2">
                    IP Changed ({diffResult.ip_changed.length})
                  </p>
                  <div className="space-y-1.5">
                    {diffResult.ip_changed.map((c, i) => (
                      <div key={i} className="bg-[#18181B] border border-[#27272A] rounded-lg px-3 py-2 text-sm flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-white truncate block">{c.worker_name}</span>
                          {c.sn && <span className="text-[10px] text-gray-500 font-mono" title="Matched by serial number">SN: {c.sn}</span>}
                        </div>
                        <span className="font-mono text-xs text-gray-400 shrink-0">{c.old_ip} → <span className="text-yellow-400">{c.new_ip}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {diffResult.worker_changed.length > 0 && (
                <div>
                  <p className="text-xs uppercase font-semibold text-cyan-400 mb-2">
                    Worker Changed ({diffResult.worker_changed.length})
                  </p>
                  <div className="space-y-1.5">
                    {diffResult.worker_changed.map((c, i) => (
                      <div key={i} className="bg-[#18181B] border border-[#27272A] rounded-lg px-3 py-2 text-sm flex items-center justify-between gap-2">
                        <div className="shrink-0">
                          <span className="font-mono text-xs text-gray-400 block">{c.ip}</span>
                          {c.sn && <span className="text-[10px] text-gray-500 font-mono" title="Matched by serial number">SN: {c.sn}</span>}
                        </div>
                        <span className="text-white truncate">{c.old_worker} → <span className="text-cyan-400">{c.new_worker}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {diffResult.went_offline.length > 0 && (
                <div>
                  <p className="text-xs uppercase font-semibold text-red-400 mb-2">
                    Went Offline ({diffResult.went_offline.length})
                  </p>
                  <div className="space-y-1.5">
                    {diffResult.went_offline.map((c, i) => (
                      <div key={i} className="bg-[#18181B] border border-[#27272A] rounded-lg px-3 py-2 text-sm flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-white truncate block">{c.worker_name || c.ip}</span>
                          {c.sn && <span className="text-[10px] text-gray-500 font-mono" title="Matched by serial number">SN: {c.sn}</span>}
                        </div>
                        <span className="text-xs text-red-400 shrink-0">{c.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerHistory;
